import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AppointmentStats {
  totalAppointments: number;
  closedDeals: number;
  conversionRate: number;
  averageCloseTimeHours: number;
  atRiskCount: number;
  atRiskPercentage: number;
  scheduledCount: number;
  cancelledCount: number;
}

export const useAppointmentStats = (
  dateRange?: { start: Date; end: Date },
  salesRepId?: string | null
) => {
  const [stats, setStats] = useState<AppointmentStats>({
    totalAppointments: 0,
    closedDeals: 0,
    conversionRate: 0,
    averageCloseTimeHours: 0,
    atRiskCount: 0,
    atRiskPercentage: 0,
    scheduledCount: 0,
    cancelledCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchStats = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('appointments')
        .select('*')
        .is('archived_at', null); // Exclude archived appointments

      // Apply date range filter
      if (dateRange) {
        query = query
          .gte('scheduled_at', dateRange.start.toISOString())
          .lte('scheduled_at', dateRange.end.toISOString());
      }

      // Apply sales rep filter
      if (salesRepId) {
        query = query.or(`sales_rep_id.eq.${salesRepId},assigned_to_sales_rep_id.eq.${salesRepId}`);
      }

      const { data: appointments, error } = await query;

      if (error) throw error;

      if (!appointments || appointments.length === 0) {
        setStats({
          totalAppointments: 0,
          closedDeals: 0,
          conversionRate: 0,
          averageCloseTimeHours: 0,
          atRiskCount: 0,
          atRiskPercentage: 0,
          scheduledCount: 0,
          cancelledCount: 0,
        });
        return;
      }

      // Calculate statistics
      const total = appointments.length;
      const closed = appointments.filter((a) => a.deal_closed).length;
      const scheduled = appointments.filter((a) => a.status === 'scheduled').length;
      const cancelled = appointments.filter((a) => a.status === 'cancelled').length;

      // Calculate at-risk appointments (scheduled appointments > 30 minutes past scheduled time without being closed)
      const now = new Date();
      const atRisk = appointments.filter((a) => {
        if (a.status !== 'scheduled' || a.deal_closed) return false;
        const scheduledTime = new Date(a.scheduled_at);
        const durationMs = a.duration_minutes * 60 * 1000;
        const appointmentEnd = new Date(scheduledTime.getTime() + durationMs);
        const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);
        return appointmentEnd < thirtyMinutesAgo;
      }).length;

      // Calculate average close time for closed deals
      let avgCloseTime = 0;
      if (closed > 0) {
        const closeTimes = appointments
          .filter((a) => a.deal_closed && a.deal_closed_at)
          .map((a) => {
            const scheduled = new Date(a.scheduled_at).getTime();
            const closedAt = new Date(a.deal_closed_at!).getTime();
            return (closedAt - scheduled) / (1000 * 60 * 60); // Convert to hours
          });

        avgCloseTime =
          closeTimes.length > 0
            ? closeTimes.reduce((sum, time) => sum + time, 0) / closeTimes.length
            : 0;
      }

      setStats({
        totalAppointments: total,
        closedDeals: closed,
        conversionRate: total > 0 ? (closed / total) * 100 : 0,
        averageCloseTimeHours: avgCloseTime,
        atRiskCount: atRisk,
        atRiskPercentage: scheduled > 0 ? (atRisk / scheduled) * 100 : 0,
        scheduledCount: scheduled,
        cancelledCount: cancelled,
      });
    } catch (error: any) {
      console.error('Error fetching appointment stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load appointment statistics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [dateRange, salesRepId]);

  return { stats, loading, refetch: fetchStats };
};
